import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import SolicitudesEstudiosService from "../../services/SolicitudesEstudiosService";
import HistorialEstudiosService from "../../services/HistorialEstudiosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Chip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const SolicitudesCanceladas = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Paginación por día
  const [paginaDia, setPaginaDia] = useState(1);

  // Cache de historial de eventos por combinación (idSolicitud-idEstudio)
  const [historialMap, setHistorialMap] = useState({});

  useEffect(() => {
    document.title = "Solicitudes Canceladas";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      // Cargar estudios para cada solicitud
      const solicitudesConEstudios = await Promise.all(
        listaSol.map(async (sol) => {
          try {
            const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(sol.idSolicitud);
            const estudiosArr = Array.isArray(resEst?.estudios)
              ? resEst.estudios
              : Array.isArray(resEst?.data)
              ? resEst.data
              : Array.isArray(resEst)
              ? resEst
              : [];
            return { ...sol, estudios: estudiosArr };
          } catch (e) {
            return { ...sol, estudios: [] };
          }
        })
      );

      setSolicitudes(solicitudesConEstudios);
      setSucursales(listaSuc);

      // Cargar historial de modificaciones para estudios cancelados en paralelo
      cargarHistorialEstudios(solicitudesConEstudios);
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorialEstudios = async (listaSol) => {
    const mapaNuevos = {};
    const promesas = [];

    listaSol.forEach((sol) => {
      const isSolCancel = isSolicitudCancelada(sol);
      (sol.estudios || []).forEach((est) => {
        const isEstCancel = isEstudioCancelado(est);
        if (isSolCancel || isEstCancel) {
          const key = `${sol.idSolicitud}_${est.idEstudio}`;
          promesas.push(
            HistorialEstudiosService.getHistorialPorEstudio(sol.idSolicitud, est.idEstudio)
              .then((hist) => {
                mapaNuevos[key] = Array.isArray(hist) ? hist : [];
              })
              .catch(() => {
                mapaNuevos[key] = [];
              })
          );
        }
      });
    });

    await Promise.all(promesas);
    setHistorialMap(mapaNuevos);
  };

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }
    return null;
  };

  const formatDateToYYYYMMDD = (dt) => {
    if (!dt) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatFechaHora = (fechaRaw) => {
    if (!fechaRaw) return "-";
    try {
      const dt = new Date(fechaRaw);
      if (isNaN(dt.getTime())) return String(fechaRaw);
      return dt.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return String(fechaRaw);
    }
  };

  // Helper de verificación de cancelación
  const isSolicitudCancelada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "C" || est === "CANCELADA" || Boolean(sol.motivoCancelacion && String(sol.motivoCancelacion).trim() !== "");
  };

  const isEstudioCancelado = (est) => {
    const estatus = `${est.estatus ?? ""}`.trim().toUpperCase();
    return estatus === "C" || estatus === "CANCELADA";
  };

  // Coincidencia de sucursal
  const coincideSucursal = (sol, sucIdSelect) => {
    if (sucIdSelect === "todas") return true;
    const sId = sol.idSucursal || sol.id_sucursal;
    return String(sId) === String(sucIdSelect);
  };

  // --- OBTENCIÓN DE TODOS LOS ELEMENTOS CANCELADOS EN EL RANGO ---
  const { solicitudesCanceladasEnRango, estudiosCanceladosEnRango, registrosCanceladosCompletos } = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);

    if (!start || !end) {
      return { solicitudesCanceladasEnRango: [], estudiosCanceladosEnRango: [], registrosCanceladosCompletos: [] };
    }

    const solSet = new Set();
    const estudiosArr = [];
    const registrosCompletos = [];

    solicitudes.forEach((sol) => {
      if (!coincideSucursal(sol, sucursalSeleccionada)) return;

      const fechaSolStr = sol.fecha || sol.fechaCreacion || sol.createdAt;
      const dtSol = parseFechaDate(fechaSolStr);
      if (!dtSol) return;
      if (dtSol.getTime() < start.getTime() || dtSol.getTime() > end.getTime()) return;

      const isSolCanc = isSolicitudCancelada(sol);

      if (isSolCanc) {
        solSet.add(sol.idSolicitud);
      }

      const listaEst = Array.isArray(sol.estudios) && sol.estudios.length > 0
        ? sol.estudios
        : [{ idEstudio: 0, nombreEstudio: "Solicitud General", estatus: sol.estatus }];

      listaEst.forEach((est) => {
        const isEstCanc = isEstudioCancelado(est);
        if (isSolCanc || isEstCanc) {
          estudiosArr.push({ sol, est });

          const nombrePaciente = [sol.nombrePaciente, sol.apPaternoPaciente, sol.apMaternoPaciente]
            .filter(Boolean)
            .join(" ")
            .trim() || sol.paciente || "Paciente";

          const key = `${sol.idSolicitud}_${est.idEstudio}`;
          const historialEvents = historialMap[key] || [];

          // Buscar eventos de Reportado (P, R, T), Revisado (V), Liberado (L)
          const evReportado = historialEvents.find((e) => ["P", "R", "T"].includes(`${e.tipoAccion}`.toUpperCase()));
          const evRevisado = historialEvents.find((e) => `${e.tipoAccion}`.toUpperCase() === "V");
          const evLiberado = historialEvents.find((e) => `${e.tipoAccion}`.toUpperCase() === "L");

          registrosCompletos.push({
            idSolicitud: sol.idSolicitud,
            codigo: sol.codigo || sol.codigoSolicitud || String(sol.idSolicitud),
            idSucursal: sol.idSucursal || sol.id_sucursal,
            nombreSucursal: sol.nombreSucursal || "Sucursal",
            paciente: nombrePaciente,
            estudioCancelado: est.nombreEstudio || est.nombre || "Estudio",
            fecha: formatDateToYYYYMMDD(dtSol),
            reportado: evReportado ? { empleado: evReportado.nombreEmpleado, fecha: evReportado.fechaModificacion } : null,
            revisado: evRevisado ? { empleado: evRevisado.nombreEmpleado, fecha: evRevisado.fechaModificacion } : null,
            liberado: evLiberado ? { empleado: evLiberado.nombreEmpleado, fecha: evLiberado.fechaModificacion } : null,
          });
        }
      });
    });

    return {
      solicitudesCanceladasEnRango: Array.from(solSet),
      estudiosCanceladosEnRango: estudiosArr,
      registrosCanceladosCompletos: registrosCompletos,
    };
  }, [solicitudes, sucursalSeleccionada, fechaDe, fechaA, historialMap]);

  // Totales para los Chips de resumen
  const totalSolicitudesCanceladas = solicitudesCanceladasEnRango.length;
  const totalEstudiosCancelados = estudiosCanceladosEnRango.length;

  // --- DÍAS CON REGISTROS PARA PAGINACIÓN DIARIA ---
  const diasConRegistros = useMemo(() => {
    const datesSet = new Set();
    registrosCanceladosCompletos.forEach((reg) => {
      if (reg.fecha) {
        datesSet.add(reg.fecha);
      }
    });
    return Array.from(datesSet).sort();
  }, [registrosCanceladosCompletos]);

  // Al cambiar filtros, reiniciar la paginación al primer día con registros
  useEffect(() => {
    setPaginaDia(1);
  }, [fechaDe, fechaA, sucursalSeleccionada]);

  const diaActivo = diasConRegistros[paginaDia - 1] || (diasConRegistros.length > 0 ? diasConRegistros[0] : fechaDe);

  // Ventana de páginas visibles (máximo 10)
  const paginasVisibles = useMemo(() => {
    const total = diasConRegistros.length;
    if (total <= 10) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let start = Math.max(1, paginaDia - 4);
    let end = start + 9;
    if (end > total) {
      end = total;
      start = Math.max(1, end - 9);
    }
    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [diasConRegistros.length, paginaDia]);

  // Registros cancelados para el DÍA ACTIVO
  const registrosDiaActivo = useMemo(() => {
    if (!diaActivo) return [];
    return registrosCanceladosCompletos.filter((reg) => reg.fecha === diaActivo);
  }, [registrosCanceladosCompletos, diaActivo]);

  // Agrupación por Sucursal para el DÍA ACTIVO
  const registrosPorSucursalDia = useMemo(() => {
    const sucsMap = {};
    const sucursalesAVisitar = sucursalSeleccionada === "todas"
      ? sucursales
      : sucursales.filter((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada));

    sucursalesAVisitar.forEach((suc) => {
      const sId = suc.idSucursal || suc.id;
      const sNom = suc.nombre || suc.nombreSucursal || `Sucursal ${sId}`;
      sucsMap[sId] = {
        idSucursal: sId,
        nombreSucursal: sNom,
        registros: [],
      };
    });

    registrosDiaActivo.forEach((reg) => {
      const sId = reg.idSucursal;
      if (sucsMap[sId]) {
        sucsMap[sId].registros.push(reg);
      } else {
        sucsMap[sId] = {
          idSucursal: sId,
          nombreSucursal: reg.nombreSucursal,
          registros: [reg],
        };
      }
    });

    // Retornar solo sucursales que tengan registros en el día activo
    return Object.values(sucsMap).filter((group) => group.registros.length > 0);
  }, [sucursales, sucursalSeleccionada, registrosDiaActivo]);

  // --- EXPORTAR A PDF (TODAS LAS SUCURSALES Y DÍAS DEL RANGO JUNTOS) ---
  const handleExportarPDF = async () => {
    if (registrosCanceladosCompletos.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen solicitudes ni estudios cancelados en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("landscape", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    // Recuadro "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(202, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 242.5, startY + 5, { align: "center" });

    // Encabezado de la Sucursal en la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 283, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 283, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 283, currY, { align: "right" });

    // Título y Período
    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("SOLICITUDES CANCELADAS", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 5);

    let nextY = currY + 12;

    // Agrupación por Sucursal de TODOS los registros en el rango (sin separar por día)
    const sucsMapCompleto = {};
    registrosCanceladosCompletos.forEach((reg) => {
      const sNom = reg.nombreSucursal || `Sucursal ${reg.idSucursal}`;
      if (!sucsMapCompleto[sNom]) {
        sucsMapCompleto[sNom] = [];
      }
      sucsMapCompleto[sNom].push(reg);
    });

    Object.entries(sucsMapCompleto).forEach(([nombreSuc, listaRegs]) => {
      const bodySuc = listaRegs.map((reg) => [
        reg.codigo,
        reg.paciente,
        reg.estudioCancelado,
        reg.reportado ? `${reg.reportado.empleado}\n${formatFechaHora(reg.reportado.fecha)}` : "-",
        reg.revisado ? `${reg.revisado.empleado}\n${formatFechaHora(reg.revisado.fecha)}` : "-",
        reg.liberado ? `${reg.liberado.empleado}\n${formatFechaHora(reg.liberado.fecha)}` : "-",
      ]);

      autoTable(doc, {
        startY: nextY,
        head: [
          [
            {
              content: `SUCURSAL: ${nombreSuc.toUpperCase()}`,
              colSpan: 6,
              styles: { halign: "left", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" },
            },
          ],
          [
            { content: "Código", styles: { halign: "left" } },
            { content: "Paciente", styles: { halign: "left" } },
            { content: "Estudio cancelado", styles: { halign: "left" } },
            { content: "Reportado", styles: { halign: "center" } },
            { content: "Revisado", styles: { halign: "center" } },
            { content: "Liberado", styles: { halign: "center" } },
          ],
        ],
        body: bodySuc,
        headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2, halign: "left", valig: "middle" },
        columnStyles: {
          0: { width: 30, fontStyle: "bold" },
          1: { width: 60 },
          2: { width: 60 },
          3: { width: 40, halign: "center" },
          4: { width: 40, halign: "center" },
          5: { width: 40, halign: "center" },
        },
      });

      nextY = doc.lastAutoTable.finalY + 8;
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 200);
      doc.text(`Página ${i} de ${pageCount}`, 283, 200, { align: "right" });
    }

        doc.save(`Solicitudes_Canceladas_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Solicitudes Canceladas en PDF...",
      "Procesando documento"
    );
  };

  // --- EXPORTAR A EXCEL ---
  const handleExportarExcel = () => {
    if (registrosCanceladosCompletos.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen solicitudes ni estudios cancelados en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    const rowsExcel = [
      [
        "Sucursal",
        "Código",
        "Paciente",
        "Estudio cancelado",
        "Reportado",
        "Revisado",
        "Liberado",
        "Fecha",
      ],
    ];

    registrosCanceladosCompletos.forEach((reg) => {
      rowsExcel.push([
        reg.nombreSucursal,
        reg.codigo,
        reg.paciente,
        reg.estudioCancelado,
        reg.reportado ? `${reg.reportado.empleado} (${formatFechaHora(reg.reportado.fecha)})` : "-",
        reg.revisado ? `${reg.revisado.empleado} (${formatFechaHora(reg.revisado.fecha)})` : "-",
        reg.liberado ? `${reg.liberado.empleado} (${formatFechaHora(reg.liberado.fecha)})` : "-",
        reg.fecha,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Solicitudes Canceladas");

    XLSX.writeFile(workbook, `Solicitudes_Canceladas_${fechaDe}_al_${fechaA}.xlsx`);
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal al estilo de los demás informes */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE SOLICITUDES CANCELADAS
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="De:"
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label="A:"
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <IconButton
                            onClick={fetchDatosIniciales}
                            sx={{ backgroundColor: "#006787", color: "white", "&:hover": { backgroundColor: "#014e65" } }}
                            title="Actualizar Datos"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Chips de Totales (Solicitudes canceladas y Cancelaciones por estudio) */}
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: "bold", color: "#18234f" }}>
                  Total de solicitudes canceladas en los días seleccionados:
                  <Chip
                    label={totalSolicitudesCanceladas}
                    sx={{ ml: 1, fontWeight: "bold", backgroundColor: "#d32f2f", color: "white" }}
                  />
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: "bold", color: "#18234f" }}>
                  Total de cancelaciones por estudio:
                  <Chip
                    label={totalEstudiosCancelados}
                    sx={{ ml: 1, fontWeight: "bold", backgroundColor: "#006787", color: "white" }}
                  />
                </Typography>
              </Stack>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={4} sx={{ width: "100%" }}>
                  {registrosPorSucursalDia.length === 0 ? (
                    <Paper elevation={1} sx={{ p: 4, textAlign: "center", color: "#64748b" }}>
                      <Typography variant="h6">
                        No existen solicitudes o estudios cancelados para la fecha seleccionada ({diaActivo}).
                      </Typography>
                    </Paper>
                  ) : (
                    registrosPorSucursalDia.map((grupoSucursal) => (
                      <Paper key={grupoSucursal.idSucursal} elevation={2} sx={{ p: 0, borderRadius: 3, overflow: "hidden", backgroundColor: "#ffffff" }}>
                        {/* Banner con el Nombre de la Sucursal */}
                        <Box sx={{ backgroundColor: "#18234f", color: "white", px: 3, py: 1.5 }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.05rem" }}>
                            SUCURSAL: {grupoSucursal.nombreSucursal.toUpperCase()}
                          </Typography>
                        </Box>

                        <Box sx={{ overflowX: "auto" }}>
                          <Table size="small" sx={{ border: "1px solid #e2e8f0" }}>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "#006787" }}>
                                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Código
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Paciente
                                </TableCell>
                                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Estudio cancelado
                                </TableCell>
                                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Reportado
                                </TableCell>
                                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Revisado
                                </TableCell>
                                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                                  Liberado
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {grupoSucursal.registros.map((row, idx) => (
                                <TableRow key={`${row.idSolicitud}_${row.estudioCancelado}_${idx}`} sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                                  <TableCell sx={{ fontWeight: "bold", color: "#006787" }}>{row.codigo}</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", color: "#1e293b" }}>{row.paciente}</TableCell>
                                  <TableCell sx={{ color: "#d32f2f", fontWeight: "bold" }}>{row.estudioCancelado}</TableCell>

                                  {/* Reportado */}
                                  <TableCell align="center">
                                    {row.reportado ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold" color="#1e293b">
                                          {row.reportado.empleado}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                          {formatFechaHora(row.reportado.fecha)}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>

                                  {/* Revisado */}
                                  <TableCell align="center">
                                    {row.revisado ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold" color="#1e293b">
                                          {row.revisado.empleado}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                          {formatFechaHora(row.revisado.fecha)}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>

                                  {/* Liberado */}
                                  <TableCell align="center">
                                    {row.liberado ? (
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold" color="#1e293b">
                                          {row.liberado.empleado}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                          {formatFechaHora(row.liberado.fecha)}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Paper>
                    ))
                  )}

                  {/* --- PAGINACIÓN POR DÍA (Estilo ReporteCaja) --- */}
                  {diasConRegistros.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        py: 2,
                        mt: 2,
                      }}
                    >
                      <IconButton
                        disabled={paginaDia <= 1}
                        onClick={() => setPaginaDia((prev) => Math.max(1, prev - 1))}
                        sx={{ backgroundColor: "#e2e8f0", "&:hover": { backgroundColor: "#cbd5e1" } }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>

                      {paginasVisibles.map((numPag) => {
                        const esActivo = numPag === paginaDia;
                        return (
                          <IconButton
                            key={numPag}
                            onClick={() => setPaginaDia(numPag)}
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "0.88rem",
                              fontWeight: "bold",
                              backgroundColor: esActivo ? "#006787" : "#f1f5f9",
                              color: esActivo ? "white" : "#334155",
                              "&:hover": {
                                backgroundColor: esActivo ? "#014e65" : "#cbd5e1",
                              },
                            }}
                          >
                            {numPag}
                          </IconButton>
                        );
                      })}

                      <IconButton
                        disabled={paginaDia >= diasConRegistros.length}
                        onClick={() => setPaginaDia((prev) => Math.min(diasConRegistros.length, prev + 1))}
                        sx={{ backgroundColor: "#e2e8f0", "&:hover": { backgroundColor: "#cbd5e1" } }}
                      >
                        <NavigateNextIcon />
                      </IconButton>

                      <Typography variant="body2" sx={{ ml: 2, color: "#64748b", fontWeight: "bold" }}>
                        Día: {diaActivo} ({paginaDia} de {diasConRegistros.length})
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botones flotantes laterales derechos para exportar PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default SolicitudesCanceladas;
